import React, { useEffect, useState, useCallback } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import useLang from '../hooks/useLang';

const STORAGE_KEY = 'auditorialegal_a2hs_dismissed';
const PROMPT_DELAY_MS = 4000;
const IOS_PROMPT_DELAY_MS = 5000;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const isMobile = () =>
  /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent);

const translations = {
  es: {
    ariaLabel: 'Instalar aplicación',
    closeAria: 'Cerrar invitación de instalación',
    title: 'Instala Auditorialegal',
    desc: 'Accede 1 toque desde tu pantalla de inicio. Sin tienda. Sin app store.',
    iosSafari: 'En Safari toca:',
    iosStep1: '1. Botón ',
    iosStep1Bold: 'Compartir',
    iosStep2: '2. ',
    iosStep2Bold: 'Añadir a pantalla de inicio',
    btnInstall: 'Instalar',
    btnNotNow: 'Ahora no'
  },
  en: {
    ariaLabel: 'Install application',
    closeAria: 'Close installation invitation',
    title: 'Install Auditorialegal',
    desc: 'Access in 1 tap from your home screen. No store. No App Store.',
    iosSafari: 'In Safari tap:',
    iosStep1: '1. ',
    iosStep1Bold: 'Share',
    iosStep1End: ' button',
    iosStep2: '2. ',
    iosStep2Bold: 'Add to Home Screen',
    btnInstall: 'Install',
    btnNotNow: 'Not now'
  }
};

export const PWAInstallPrompt = () => {
  const lang = useLang();
  const t = translations[lang];

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosFlow, setIosFlow] = useState(false);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    setShow(false);
  }, []);

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (sessionStorage.getItem(STORAGE_KEY)) return undefined;
    if (!isMobile()) return undefined;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), PROMPT_DELAY_MS);
    };
    window.addEventListener('beforeinstallprompt', handler);

    let iosTimer;
    if (isIOS()) {
      setIosFlow(true);
      iosTimer = setTimeout(() => setShow(true), IOS_PROMPT_DELAY_MS);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setShow(false);
    } else {
      dismiss();
    }
    setDeferredPrompt(null);
  }, [deferredPrompt, dismiss]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[9997] sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-[380px] rounded-2xl shadow-2xl border-2 border-[#D4AF37]/40 bg-gradient-to-br from-[#1B2A4A] to-[#2a3b66] text-white overflow-hidden"
      role="dialog"
      aria-label={t.ariaLabel}
      data-testid="pwa-install-prompt"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label={t.closeAria}
        className="absolute top-2 right-2 text-white/60 hover:text-white p-1"
        data-testid="pwa-prompt-close"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
            <Download className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm">{t.title}</p>
            <p className="text-xs text-white/75 mt-0.5 leading-snug">
              {t.desc}
            </p>
          </div>
        </div>

        {iosFlow ? (
          <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/85 leading-relaxed">
            <p className="mb-2">{t.iosSafari}</p>
            <ol className="space-y-1.5 ml-1">
              <li className="flex items-center gap-2">
                <span className="inline-flex w-5 h-5 items-center justify-center rounded bg-white/10">
                  <Share className="w-3 h-3 text-[#D4AF37]" />
                </span>
                <span>
                  {t.iosStep1}<strong>{t.iosStep1Bold}</strong>{t.iosStep1End}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex w-5 h-5 items-center justify-center rounded bg-white/10">
                  <Plus className="w-3 h-3 text-[#D4AF37]" />
                </span>
                <span>
                  {t.iosStep2}<strong>{t.iosStep2Bold}</strong>
                </span>
              </li>
            </ol>
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={install}
              data-testid="pwa-prompt-install"
              className="flex-1 px-3 py-2 rounded-lg bg-[#D4AF37] hover:bg-[#b89426] text-[#1B2A4A] font-semibold text-sm transition-colors"
            >
              {t.btnInstall}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="px-3 py-2 rounded-lg border border-white/20 hover:bg-white/10 text-sm text-white/80 transition-colors"
            >
              {t.btnNotNow}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
